"use client"

import { useCallback, useEffect, useState } from "react"
import { useAuth } from "@/hooks/use-auth"
import type {
  CrossSellViewMode,
  CrossSellGroupBy,
  CoPurchasePair,
  FocusProductSummary,
  BundleAnalysis,
  FamilyPair,
  FamilyFocusSummary,
} from "./types"

interface UseCrossSellDataOptions {
  integrationId: string
  monthStartUtc: string
  monthEndUtc: string
  mode: CrossSellViewMode
  /** Default "product". "family" toggles Pairs/Focus to family-keyed shapes. */
  groupBy?: CrossSellGroupBy
  focusProductIds?: number[]
  focusFamilyKeys?: string[]
  /** Each element is a group of product IDs (pipe-separated to API) */
  bundleGroups?: number[][]
  minimumOrderTotal?: number
  limit?: number
}

interface UseCrossSellDataResult {
  pairs: CoPurchasePair[]
  pairsByFamily: FamilyPair[]
  focus: FocusProductSummary | null
  focusByFamily: FamilyFocusSummary | null
  bundle: BundleAnalysis | null
  isLoading: boolean
  error: string | null
  refresh: () => Promise<void>
}

export function useCrossSellData({
  integrationId,
  monthStartUtc,
  monthEndUtc,
  mode,
  groupBy = "product",
  focusProductIds,
  focusFamilyKeys,
  bundleGroups,
  minimumOrderTotal = 0,
  limit = 50,
}: UseCrossSellDataOptions): UseCrossSellDataResult {
  const { fetchWithAuth, isAuthenticated } = useAuth()
  const [pairs, setPairs] = useState<CoPurchasePair[]>([])
  const [pairsByFamily, setPairsByFamily] = useState<FamilyPair[]>([])
  const [focus, setFocus] = useState<FocusProductSummary | null>(null)
  const [focusByFamily, setFocusByFamily] = useState<FamilyFocusSummary | null>(null)
  const [bundle, setBundle] = useState<BundleAnalysis | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!integrationId || !isAuthenticated || !monthStartUtc || !monthEndUtc) {
      return
    }

    // Compare Bundles owns its own data flow (useBundlePerformance).
    // useCrossSellData stays inert for that mode — clear sibling state and
    // bail before issuing the legacy /cross-sell request.
    if (mode === "compare") {
      setPairs([])
      setPairsByFamily([])
      setFocus(null)
      setFocusByFamily(null)
      setBundle(null)
      setError(null)
      return
    }

    // Clear stale data and skip fetch when mode-specific params are not yet set
    if (mode === "focus") {
      if (groupBy === "product" && (!focusProductIds || focusProductIds.length === 0)) {
        setFocus(null)
        setFocusByFamily(null)
        setError(null)
        return
      }
      if (groupBy === "family" && (!focusFamilyKeys || focusFamilyKeys.length === 0)) {
        setFocus(null)
        setFocusByFamily(null)
        setError(null)
        return
      }
    }
    const filledGroups = bundleGroups?.filter((g) => g.length > 0)
    if (mode === "bundle" && (!filledGroups || filledGroups.length < 2)) {
      setBundle(null)
      setError(null)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const params = new URLSearchParams({
        integration_id: integrationId,
        month_start_utc: monthStartUtc,
        month_end_utc: monthEndUtc,
        mode,
        group_by: groupBy,
        minimum_order_total: String(minimumOrderTotal),
        limit: String(limit),
      })

      if (mode === "focus") {
        if (groupBy === "product" && focusProductIds && focusProductIds.length > 0) {
          params.set("focus_product_ids", focusProductIds.join(","))
        } else if (groupBy === "family" && focusFamilyKeys && focusFamilyKeys.length > 0) {
          params.set("focus_family_keys", focusFamilyKeys.join(","))
        }
      }
      if (mode === "bundle" && filledGroups && filledGroups.length >= 2) {
        params.set("bundle_product_ids", filledGroups.map((g) => g.join(",")).join("|"))
      }

      const res = await fetchWithAuth(`/api/integrations/shopify/cross-sell?${params}`)

      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: "Request failed" }))
        setError(data.error ?? "Request failed")
        // Clear stale data so UI shows error, not outdated results
        setPairs([])
        setPairsByFamily([])
        setFocus(null)
        setFocusByFamily(null)
        setBundle(null)
        return
      }

      const data = await res.json()

      switch (mode) {
        case "pairs":
          if (groupBy === "family") {
            setPairsByFamily(data.pairs_by_family ?? [])
            setPairs([])
          } else {
            setPairs(data.pairs ?? [])
            setPairsByFamily([])
          }
          setFocus(null)
          setFocusByFamily(null)
          setBundle(null)
          break
        case "focus":
          if (groupBy === "family") {
            setFocusByFamily(data.focus_by_family ?? null)
            setFocus(null)
          } else {
            setFocus(data.focus ?? null)
            setFocusByFamily(null)
          }
          setPairs([])
          setPairsByFamily([])
          setBundle(null)
          break
        case "bundle":
          setBundle(data.bundle ?? null)
          setPairs([])
          setPairsByFamily([])
          setFocus(null)
          setFocusByFamily(null)
          break
      }
    } catch (err) {
      console.error("[CrossSell] Fetch error:", err)
      setError("Failed to load cross-sell data")
      setPairs([])
      setPairsByFamily([])
      setFocus(null)
      setFocusByFamily(null)
      setBundle(null)
    } finally {
      setIsLoading(false)
    }
  }, [
    integrationId,
    monthStartUtc,
    monthEndUtc,
    mode,
    groupBy,
    focusProductIds,
    focusFamilyKeys,
    bundleGroups,
    minimumOrderTotal,
    limit,
    isAuthenticated,
    fetchWithAuth,
  ])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return {
    pairs,
    pairsByFamily,
    focus,
    focusByFamily,
    bundle,
    isLoading,
    error,
    refresh,
  }
}
